import React from 'react'
import { useLocation } from 'react-router-dom'

import { useAppStore } from '@runtime/core/store/appStore'
import { commitPosterThemeSelection } from '@runtime/core/theme/commitPosterThemeSelection'
import {
  canAccessPosterTheme,
  isDynamicPosterPrivate,
  isPosterPrivate,
  setPosterThemeAccessToken,
} from '@runtime/core/theme/posterAccess'
import { requestPosterThemeAccess } from '@runtime/core/theme/posterThemeAccessApi'
import { readThemeQueryValueFromSearch } from '@runtime/core/theme/themeQuery'
import {
  precacheThemeAssets,
  resolveTheme,
  resolveThemeIdFromQueryValue,
} from '@runtime/core/theme/posterThemes'

export function useUrlThemeSelection(enabled = true) {
  const location = useLocation()
  const requestRef = React.useRef(0)

  React.useEffect(() => {
    if (!enabled) return
    const themeQueryValue = readThemeQueryValueFromSearch(location.search)
    if (!themeQueryValue) return

    const requestId = requestRef.current + 1
    requestRef.current = requestId

    const applyThemeFromUrl = async () => {
      const themeId = await resolveThemeIdFromQueryValue(themeQueryValue)
      if (!themeId || requestRef.current !== requestId) return

      const { user, authToken, setThemeId, addPosterPrivateUnlockedId } =
        useAppStore.getState()
      if (!canAccessPosterTheme(themeId, user)) {
        if (!isPosterPrivate(themeId)) return
        if (isDynamicPosterPrivate(themeId)) {
          const accessToken = await requestPosterThemeAccess({
            themeId,
            authToken,
            publicSlug: themeQueryValue,
          }).catch(() => null)
          if (!accessToken || requestRef.current !== requestId) return
          setPosterThemeAccessToken(themeId, accessToken)
        } else {
          addPosterPrivateUnlockedId(themeId)
        }
      }

      const cfg = await resolveTheme(themeId)
      if (!cfg || requestRef.current !== requestId) return

      await precacheThemeAssets(themeId, cfg)
      if (requestRef.current !== requestId) return

      commitPosterThemeSelection({ themeId, cfg, setThemeId })
    }

    void applyThemeFromUrl()
    return () => {
      requestRef.current += 1
    }
  }, [enabled, location.pathname, location.search])
}
